import cardExpiredImage from 'core/common/assets/images/payment-errors/card-expired.svg';
import CVVImage from 'core/common/assets/images/payment-errors/cvv.svg';
import debitCardImage from 'core/common/assets/images/payment-errors/debit-card.svg';
import insufficientFundsImage from 'core/common/assets/images/payment-errors/insufficient-funds.svg';
import limitExceededImage from 'core/common/assets/images/payment-errors/limit-exceeded.svg';
import processingImage from 'core/common/assets/images/payment-errors/processing.svg';
import wrongNumberImage from 'core/common/assets/images/payment-errors/wrong-number.svg';
import { Typography, Button } from 'core/common/ui/ui-kit';
import { prepareHtml } from 'core/common/utils/prepareHtml';
import { PaymentContent } from 'core/flow/entities/Content';
import { PaymentError as PaymentFormError, PaymentErrorCode } from 'core/payments/entities';
import { CaptionStyled, ImageStyled, SolutionStyled } from './styled';

const getErrorData = (content: PaymentContent, paymentError: PaymentFormError | null) => {
  const code = paymentError?.code;

  if (code === PaymentErrorCode.DEBIT_CARD_NOT_SUPPORTED) {
    return {
      image: {
        src: debitCardImage,
        alt: 'debit card error',
      },
      title: content.errorDebitCardTitle,
      solution: content.errorDebitCardSolution,
    };
  }
  if (code === PaymentErrorCode.INVALID_EXPIRATION_DATE) {
    return {
      image: {
        src: cardExpiredImage,
        alt: 'card expired error',
      },
      title: content.errorCardExpiredTitle,
      solution: content.errorCardExpiredSolution,
    };
  }
  if (code === PaymentErrorCode.INVALID_CARD_NUMBER) {
    return {
      image: {
        src: wrongNumberImage,
        alt: 'invalid card number-error',
      },
      title: content.errorWrongNumberTitle,
      solution: content.errorWrongNumberSolution,
    };
  }
  if (code === PaymentErrorCode.INSUFFICIENT_FUNDS) {
    return {
      image: {
        src: insufficientFundsImage,
        alt: 'insufficient funds error',
      },
      title: content.errorInsufficientFundsTitle,
      solution: content.errorInsufficientFundsSolution,
    };
  }
  if (code === PaymentErrorCode.INVALID_CVV2_CODE) {
    return {
      image: {
        src: CVVImage,
        alt: 'invalid svv error',
      },
      title: content.errorCVVTitle,
      solution: content.errorCVVSolution,
      tip: content.errorCVVTip,
    };
  }
  if (code === PaymentErrorCode.PAYMENT_AMOUNT_LIMIT_EXCESS) {
    return {
      image: {
        src: limitExceededImage,
        alt: 'limit excess error',
      },
      title: content.errorLimitTitle,
      solution: content.errorLimitSolution,
    };
  }
  return {
    image: {
      src: processingImage,
      alt: 'processing error',
    },
    title: content.errorGeneralTitle,
    solution: content.errorGeneralSolution,
  };
};

export type PaymentFormFailedProps = {
  onTryAgain?: () => void;
  paymentError: PaymentFormError | null;
  content: PaymentContent;
};

export const PaymentFormFailed = ({
  onTryAgain = () => {},
  paymentError,
  content,
}: PaymentFormFailedProps) => {
  const { image, title, solution, tip } = getErrorData(content, paymentError);

  return (
    <>
      <ImageStyled width={80} height={80} src={image.src} alt={image.alt} />
      {title && (
        <Typography variant="h3" gutterBottom dangerouslySetInnerHTML={prepareHtml(title)} />
      )}
      {solution && (
        <SolutionStyled
          variant="bodyMediumRegular"
          dangerouslySetInnerHTML={prepareHtml(solution)}
        />
      )}
      {content.errorGeneralCaption && (
        <CaptionStyled
          variant="bodyMediumRegular"
          gutterBottom
          dangerouslySetInnerHTML={prepareHtml(content.errorGeneralCaption)}
        />
      )}
      {tip && (
        <CaptionStyled
          variant="bodySmallRegular"
          gutterBottom
          dangerouslySetInnerHTML={prepareHtml(tip)}
        />
      )}
      <Button fullWidth variant="contained" onClick={onTryAgain}>
        {content.errorButtonText}
      </Button>
    </>
  );
};
